import React, { useState } from "react";
import { Check, Home, User } from "lucide-react";

import { REPORT_SECTIONS, REPORT_TEMPLATES } from "../cmaReportTypes";
import CmaAppraiseStep from "./CmaAppraiseStep";
import CmaInsightsStep from "./CmaInsightsStep";

const TABS = [
  { key: "cover", label: "Report Cover Page" },
  { key: "agent", label: "Agent Profile" },
  { key: "sections", label: "Report Sections" },
];
const BLURB_MAX = 450;

// A small picture of each cover so the choice is visual, as on Property Data.
const TemplatePreview = ({ template, theme }) => {
  if (template === "modern") {
    return (
      <div className="h-24 rounded bg-white border border-gray-100 overflow-hidden">
        <div className="h-7" style={{ backgroundColor: theme }} />
        <div className="p-2 space-y-1"><div className="h-6 bg-gray-200 rounded" /><div className="h-1.5 bg-gray-100 rounded w-2/3" /></div>
      </div>
    );
  }
  if (template === "classic") {
    return (
      <div className="h-24 rounded bg-white border border-gray-100 p-2 flex flex-col items-center gap-1">
        <div className="h-2 w-1/2 rounded" style={{ backgroundColor: theme }} />
        <div className="h-10 w-full bg-gray-200 rounded" />
        <div className="h-1.5 w-1/3 bg-gray-100 rounded" />
      </div>
    );
  }
  return (
    <div className="h-24 rounded bg-white border border-gray-100 p-2 space-y-1.5">
      <div className="h-1.5 w-1/4 rounded" style={{ backgroundColor: theme }} />
      <div className="h-2 w-2/3 bg-gray-300 rounded" />
      <div className="h-10 w-1/2 bg-gray-100 rounded" />
    </div>
  );
};

/**
 * Report Setup, the last step of a Sales or Rental CMA on Property Data. The
 * agent picks the cover, the agent profile and which sections go in.
 *
 * Our own extras sit inside Report Sections rather than as extra steps:
 * Demographics & Median Prices shows the suburb tiles to choose from, and
 * Price Estimate holds the appraisal range and comment.
 */
const CmaReportSetup = ({
  report, setup, onChange, insights, onInsightsChange, appraisal, onAppraisalChange, comparables, rental,
}) => {
  const [tab, setTab] = useState("cover");
  const theme = setup.theme_colour || "#1f2a44";
  const sections = setup.sections || {};
  const isOn = (key) => sections[key] !== false;
  const toggle = (key) => onChange({ sections: { ...sections, [key]: !isOn(key) } });
  const groupCount = (group) => comparables.filter((c) => c.group === group).length;

  return (
    <div>
      <div className="flex flex-wrap gap-1 border-b border-gray-200 mb-5" role="tablist" aria-label="Report setup">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm -mb-px border-b-2 ${
              tab === t.key ? "border-[#1f2a44] font-semibold text-gray-900" : "border-transparent text-gray-600 hover:text-gray-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "cover" && (
        <div className="space-y-6">
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h3 className="text-lg text-gray-900">Select a CMA Report template...</h3>
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                Theme colour:
                <input
                  type="color"
                  value={theme}
                  onChange={(e) => onChange({ theme_colour: e.target.value })}
                  aria-label="Theme colour"
                  className="w-9 h-9 rounded border border-gray-200 bg-white"
                />
                <span className="font-mono text-xs text-gray-500">{theme}</span>
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {REPORT_TEMPLATES.map((t) => {
                const active = (setup.template || "modern") === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => onChange({ template: t.key })}
                    className={`text-left rounded-lg border-2 p-2 transition-colors ${active ? "" : "border-gray-100 hover:border-gray-200"}`}
                    style={active ? { borderColor: theme } : undefined}
                  >
                    <TemplatePreview template={t.key} theme={theme} />
                    <span className="mt-2 flex items-center justify-between text-sm font-semibold text-gray-900">
                      {t.label} {active && <Check className="w-4 h-4" style={{ color: theme }} />}
                    </span>
                    <span className="block text-xs text-gray-500">{t.hint}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-lg text-gray-900 mb-3">Select report cover photo...</h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="w-full sm:w-64 h-40 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                {setup.image_url
                  ? <img src={setup.image_url} alt="Cover" className="w-full h-full object-cover" />
                  : <Home className="w-8 h-8 text-gray-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <label htmlFor="cma-cover-url" className="block text-xs font-semibold text-gray-600 mb-1">Cover photo link</label>
                <input
                  id="cma-cover-url"
                  type="url"
                  value={setup.image_url || ""}
                  onChange={(e) => onChange({ image_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  A Collings listing brings its own photo. For any other property, paste a link to a photo.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "agent" && (
        <div className="max-w-2xl space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100">
            <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center">
              {report?.agent?.profile_pic_url
                ? <img src={report.agent.profile_pic_url} alt="" className="w-full h-full object-cover" />
                : <User className="w-5 h-5 text-gray-400" />}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{report?.agent?.name || "Lead agent"}</p>
              <p className="text-xs text-gray-500">{[report?.agent?.phone_number, report?.agent?.email].filter(Boolean).join(" · ")}</p>
            </div>
          </div>
          <div>
            <label htmlFor="cma-agent-blurb" className="block text-sm font-semibold text-gray-700 mb-1">Agent Profile</label>
            <textarea
              id="cma-agent-blurb"
              rows={6}
              maxLength={BLURB_MAX}
              value={setup.agent_blurb || ""}
              onChange={(e) => onChange({ agent_blurb: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
            />
            <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
              <button type="button" onClick={() => onChange({ agent_blurb: "" })} className="underline">Clear</button>
              <span>{BLURB_MAX - (setup.agent_blurb || "").length} characters remaining</span>
            </div>
          </div>
          <p className="text-xs text-gray-500">The photo is the agent&apos;s CRM profile picture.</p>
        </div>
      )}

      {tab === "sections" && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg text-gray-900 mb-3">Include the following sections in the report...</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {REPORT_SECTIONS.map((section) => {
                const n = section.group ? groupCount(section.group) : null;
                const label = rental ? section.label.replace("Sales", "Rentals") : section.label;
                return (
                  <label key={section.key} className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-gray-100 bg-white cursor-pointer">
                    <span className="inline-flex items-center gap-2 text-sm text-gray-800">
                      <input type="checkbox" checked={isOn(section.key)} onChange={() => toggle(section.key)} />
                      {label}
                    </span>
                    {n != null && <span className="px-1.5 rounded bg-[#1f2a44] text-white text-[11px] font-bold">{n}</span>}
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2">Additional Options</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
              <div>
                <label htmlFor="cma-prepared-for" className="block text-xs font-semibold text-gray-600 mb-1">Prepared for</label>
                <input id="cma-prepared-for" type="text" value={setup.recipient_name || ""}
                  onChange={(e) => onChange({ recipient_name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
              </div>
              <div>
                <label htmlFor="cma-prepared-by" className="block text-xs font-semibold text-gray-600 mb-1">Prepared by</label>
                <input id="cma-prepared-by" type="text" value={setup.prepared_by || ""}
                  onChange={(e) => onChange({ prepared_by: e.target.value })}
                  placeholder={report?.agent?.name || ""}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
              </div>
            </div>
          </div>

          {isOn("market") && (
            <div className="pt-4 border-t border-gray-100">
              <CmaInsightsStep report={report} insights={insights} onChange={onInsightsChange} />
            </div>
          )}

          {isOn("estimate") && (
            <div className="pt-4 border-t border-gray-100">
              <CmaAppraiseStep report={report} form={appraisal} onChange={onAppraisalChange} comparables={comparables} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CmaReportSetup;
